import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { applyMiddleware, compose, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import thunk from 'redux-thunk';
import App from './App';
import { i18nReady } from './locales/i18n';
import reportWebVitals from './reportWebVitals';
import { rootReducer } from './states';
import { startClsDebug } from './utils/clsDebug';

// Every layout shift, live, with the DOM node that moved — dev always; in a
// prod build opt in with localStorage.setItem('pmq:clsDebug', '1') + reload.
try {
  if (process.env.NODE_ENV === 'development' || window.localStorage.getItem('pmq:clsDebug') === '1') {
    startClsDebug();
  }
} catch (e) {}

// DevTools wiring only in development — in production it's dead weight on the
// boot path and every dispatch pays its serialization hook if a merchant
// happens to have the extension installed.
const composeEnhancers =
  process.env.NODE_ENV === 'development' ? composeWithDevTools({}) : compose;
const store = createStore(rootReducer, composeEnhancers(applyMiddleware(thunk)));

const root = createRoot(document.getElementById('root'));
const render = () =>
  root.render(
    <Provider store={store}>
      <App />
    </Provider>
  );

// Only English is bundled; the other five locales are separate chunks (see
// locales/i18n.js). i18nReady resolves on the same tick for English — the
// common case pays nothing — and for the other five it's one small fetch that
// we'd rather wait on than paint the whole UI in English and swap it, which
// would be a page-wide layout shift right inside the CLS window.
i18nReady.then(render).catch(render);

// Mirror the vitals into our own console + analytics alongside App Bridge's
// reporting to Shopify, so a regression is visible here before it moves the
// Built for Shopify numbers. Same setup as Discount Prime's index.js.
//
// Per-metric attribution fields (web-vitals/attribution build) — lets the
// console line point straight at the offending element/interaction instead of
// just a number. Field names come from each metric's *Attribution type.
function attributionFields(metric) {
  const a = metric.attribution || {};
  switch (metric.name) {
    case 'CLS':
      return {
        target: a.largestShiftTarget,
        shiftValue: a.largestShiftValue,
        shiftTime: a.largestShiftTime,
        loadState: a.loadState,
      };
    case 'LCP':
      return {
        target: a.element,
        url: a.url,
        timeToFirstByte: a.timeToFirstByte,
        resourceLoadDelay: a.resourceLoadDelay,
        resourceLoadTime: a.resourceLoadTime,
        elementRenderDelay: a.elementRenderDelay,
      };
    case 'INP':
      return {
        target: a.interactionTarget,
        interactionType: a.interactionType,
        inputDelay: a.inputDelay,
        processingDuration: a.processingDuration,
        presentationDelay: a.presentationDelay,
      };
    default:
      return {};
  }
}

// `target` above is a CSS selector string, not a live node — by the time
// anyone re-queries it by hand in the console, a swapped-out loading→loaded
// element is usually already gone. Resolve it right now, at report time, and
// capture its own text so the log is self-contained.
function describeTarget(selector) {
  if (!selector || typeof document === 'undefined') return {};
  let el;
  try {
    el = document.querySelector(selector);
  } catch (e) {
    return {};
  }
  if (!el) return { targetText: '(not in DOM anymore)' };
  return {
    targetText: el.textContent?.trim().slice(0, 120),
    targetHTML: el.outerHTML?.slice(0, 200),
  };
}

// `rating` is web-vitals' own good/needs-improvement/poor bucket, computed
// against the same thresholds the Built for Shopify benchmark uses
// (LCP 2.5s, CLS 0.1, INP 200ms) — worth sending as its own dimension so a
// dashboard can just count "poor" instead of re-deriving the cutoffs.
reportWebVitals((metric) => {
  const { name, value, delta, id, rating } = metric;
  const fields = attributionFields(metric);
  Object.assign(fields, describeTarget(fields.target));

  // Always logged, prod included (same as Discount Prime): a slow merchant
  // report is debuggable from a screenshot of their console instead of a
  // devtools re-measure, and the line points at the exact element.
  // eslint-disable-next-line no-console
  console.log(`[WebVitals] ${name}`, Math.round(name === 'CLS' ? value * 1000 : value), rating, fields);

  const payload = {
    metric_name: name,
    // CLS ×1000 so every metric ships as an integer (0.14 → 140).
    metric_value: Math.round(name === 'CLS' ? value * 1000 : value),
    metric_delta: Math.round(name === 'CLS' ? delta * 1000 : delta),
    metric_id: id,
    metric_rating: rating,
    metric_path: window.location.pathname,
    metric_target: fields.target,
    // LCP's breakdown is the one that explains WHY across real sessions —
    // console-only attribution can't be queried after the fact, so it rides
    // along to GTM/Heap too.
    ...(name === 'LCP' && {
      metric_time_to_first_byte: fields.timeToFirstByte,
      metric_resource_load_delay: fields.resourceLoadDelay,
      metric_resource_load_time: fields.resourceLoadTime,
      metric_element_render_delay: fields.elementRenderDelay,
    }),
    ...(name === 'CLS' && {
      metric_shift_value: fields.shiftValue,
      metric_load_state: fields.loadState,
    }),
    ...(name === 'INP' && {
      metric_interaction_type: fields.interactionType,
      metric_input_delay: fields.inputDelay,
      metric_processing_duration: fields.processingDuration,
      metric_presentation_delay: fields.presentationDelay,
    }),
  };

  // GTM/Heap load lazily after first paint (see public/index.html), and the
  // vitals can fire before or after that — hence the defensive checks. Both
  // queue-style APIs are safe to call more than once.
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'web-vitals', ...payload });
  } catch (e) {}

  try {
    window.heap?.track?.('web-vitals', payload);
  } catch (e) {}
});
